import React from 'react';
import { linkDataText } from './DetailSection';
import formatScalar from './formatValue';
import { htmlToText } from '../../utils/formatEntry';

/**
 * The list card's body, rendered from the Family's derived card BLOCKS
 * (allowlist A35's layout knob — see `deriveCardBlocks`).
 *
 * Four block kinds, and each one is a shape rather than a page:
 *
 *   rows      `Label: value` lines — the default, and what every card body was
 *             before this slice
 *   grid-4    a four-up meta grid with an em-dash for an absent value, which is
 *             the spell card's casting time / range / duration / saving throw
 *   icon-row  the group's boolean members as on/off abbreviation icons, which
 *             is the spell card's V S M F DF XP row
 *   link-list a reference set as `name value` pairs — a spell's class levels
 *
 * `grid-4` prints `—` where the other three self-hide, and that is deliberate
 * rather than an inconsistency: a fixed grid whose cells vanish stops being a
 * grid, so the shipped card printed the dash and so does this.
 */

/**
 * A card row's display text.
 *
 * A reference contributes its targets' NAMES, comma-joined — the shipped deity
 * card printed `Domains: Earth, Law, Nobility`, and `String(value)` on an array
 * of target objects would print `[object Object]`. It is the same rule
 * `AttrValue` applies on the detail; a card row is not a different kind of
 * thing, it just carries no links.
 */
function rowText(attr, value) {
  if (Array.isArray(value)) {
    return value.map((v) => (v && typeof v === 'object' ? v.name : v)).filter(Boolean).join(', ');
  }
  if (value && typeof value === 'object') return value.name || '';
  // A card carries no markup: rich text (a condition's description, a skill's
  // Untrained note) prints as its words, never as "<p>The character…".
  if (attr.datatype === 'richtext') return htmlToText(value).replace(/\s+/g, ' ');
  return formatScalar(attr, value);
}

/**
 * Which rows print WITHOUT a label — the description register and nothing else.
 *
 * `isProse` is the wrong test here and R9 is where the difference shows: it is
 * true of any attribute claiming the prose render style, and Skills declares
 * nine of those while its shipped card prints `Untrained: …` with its label. The
 * card's rule is narrower than the detail's — the unlabelled text on a card is
 * the entity's description, which is the register's whole meaning.
 */
const DESCRIPTION_REGISTER = 'description';

function Rows({ block, row }) {
  const present = block.members.filter((attr) => {
    const value = row[attr.key];
    if (value === undefined || value === null || value === '') return false;
    return !Array.isArray(value) || value.length > 0;
  });
  if (!present.length) return null;
  return (
    <div className="text-sm text-secondary space-y-1">
      {present.map((attr) => (
        attr.register === DESCRIPTION_REGISTER ? (
          <div key={attr.key} className="text-secondary">{rowText(attr, row[attr.key])}</div>
        ) : (
          <div key={attr.key}><strong>{attr.label}:</strong> {rowText(attr, row[attr.key])}</div>
        )
      ))}
    </div>
  );
}

function Grid4({ block, row }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-secondary">
      {block.members.map((attr) => (
        <div key={attr.key}>
          <span className="font-medium">{attr.label}:</span> {rowText(attr, row[attr.key]) || '—'}
        </div>
      ))}
    </div>
  );
}

function IconRow({ block, row }) {
  const flags = block.members.filter((m) => m.datatype === 'boolean' && m.abbr);
  if (!flags.length) return null;
  return (
    <div className="flex items-center space-x-4 text-sm">
      {block.label && <span className="font-medium">{block.label}:</span>}
      <div className="page-header">
        {flags.map((attr) => (
          <span
            key={attr.key}
            className={row[attr.key] ? 'spell-component-icon' : 'spell-component-icon-inactive'}
          >
            {attr.abbr}
          </span>
        ))}
      </div>
    </div>
  );
}

function LinkListBlock({ block, row }) {
  const targets = row[block.attr.key];
  if (!Array.isArray(targets) || !targets.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {targets.map((target, i) => (
        <div key={target.slug || i} className="flex items-center space-x-1">
          <span className="text-sm font-medium">{target.name}</span>
          <span className="text-sm text-danger">{linkDataText(block.attr, target)}</span>
        </div>
      ))}
    </div>
  );
}

const BLOCKS = {
  rows: Rows,
  'grid-4': Grid4,
  'icon-row': IconRow,
  'link-list': LinkListBlock,
};

export const IMPLEMENTED_CARD_BLOCKS = new Set(Object.keys(BLOCKS));

export default function ListCardBody({ blocks, row }) {
  return (
    <>
      {blocks.map((block, i) => {
        const Block = BLOCKS[block.kind];
        if (!Block) return null;
        return <Block key={block.group || block.attr?.key || `block-${i}`} block={block} row={row} />;
      })}
    </>
  );
}
